import apis from './apis';

/**
 * Contract Lifecycle (CLM) API client.
 *
 * A contract is a thin record on an existing document (draft/file). These power
 * the CLM dashboard: every contract with its lifecycle status, source alignment,
 * review risk, and the upcoming-renewals list.
 */

export type ContractStatus = 'active' | 'expiring_soon' | 'expired' | 'unknown';
export type ContractRiskLevel = 'low' | 'medium' | 'high';

export interface ContractParty {
  name?: string;
  role?: string;
}

export interface ContractRiskFlag {
  title?: string;
  severity?: 'low' | 'medium' | 'high';
  detail?: string;
}

/** Web PubSub connection block for live extraction updates. */
export interface ContractPubSub {
  token?: string;
  endpoint?: string;
  hub?: string;
  group?: string;
}

export interface ContractRow {
  uuid: string;
  document_type: 'draft' | 'file';
  document_uuid: string;
  title: string;
  status: ContractStatus;
  days_to_expiry: number | null;
  effective_date: string | null;
  expiry_date: string | null;
  auto_renew: boolean | null;
  renewal_notice_days: number | null;
  governing_law: string | null;
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  parties: ContractParty[];
  contract_type?: string | null;
  risk_level?: ContractRiskLevel | null;
  intelligence_status?: string | null;
  risk?: { reviewed: boolean; open_findings: number };
  needs_attention?: boolean;
  /** Linkage - set when the contract belongs to a matter/project. */
  matter_uuid?: string | null;
  project_uuid?: string | null;
}

export interface UpcomingRenewal {
  contract_uuid: string;
  title: string;
  expiry_date: string;
  days_to_expiry: number | null;
  status: ContractStatus;
}

export interface ContractsDashboard {
  contracts: ContractRow[];
  upcoming_renewals: UpcomingRenewal[];
  counts: {
    total: number; active: number; expiring_soon: number; expired: number; needs_attention: number;
  };
}

export async function getContractsDashboard(): Promise<ContractsDashboardWithPubsub> {
  const res = await apis.get('/contracts/dashboard');
  return (res.data?.data ?? res.data) as ContractsDashboardWithPubsub;
}

export interface IntelligenceSignal {
  kind: string;               // 'template' | 'branding' | 'playbook'
  status: string;             // 'aligned' | 'behind' | ...
  action?: string | null;
  from_version?: number | null;
  to_version?: number | null;
  review_session_uuid?: string | null;
}

export type ObligationStatus = 'open' | 'done' | 'waived';

/** The linked task's live state, reflected onto the obligation (read-only). */
export interface ObligationTaskRef {
  uuid: string | null;
  status: string;              // task status: todo | in_progress | done | ...
  due_date: string | null;
  assigned_to_user_id: number | null;
}

export interface ContractObligation {
  uuid: string;
  contract_uuid: string;
  description: string;
  due_date: string | null;
  status: string;             // reflected from the task when tracked, else obligation status
  owner_user_id: number | null;
  source: 'manual' | 'extracted';
  overdue: boolean;
  created_at: string | null;
  /** Whether a live task is tracking this obligation. */
  tracked?: boolean;
  task_uuid?: string | null;  // present only when tracked
  task?: ObligationTaskRef | null;
}

export interface ContractReminderItem {
  stage: string;            // renewal_d30 | renewal_notice | renewal_d7 | expired
  sent_at: string | null;
  reference_date: string | null;
}

export interface ContractDetail extends ContractRow {
  key_obligations: string[];
  obligations?: ContractObligation[];
  reminders?: ContractReminderItem[];
  /** For a file-type contract: the uuid of the folder the file lives in, so
   *  "Open document" can deep-link to the file viewer. Null if unknown/root. */
  file_folder_uuid?: string | null;
  contract_value: string | null;
  // Document-intelligence fields.
  summary?: string | null;
  term_description?: string | null;
  payment_terms?: string | null;
  termination_summary?: string | null;
  dispute_resolution?: string | null;
  risk_summary?: string | null;
  risk_flags?: ContractRiskFlag[];
  /** True when only head+tail of a long document was analysed - dates may be incomplete. */
  source_truncated?: boolean;
  extracted_at?: string | null;
  intelligence?: { status?: string; headline?: string; signals?: IntelligenceSignal[] } | null;
  pubsub?: ContractPubSub | null;
}

export interface ContractsDashboardWithPubsub extends ContractsDashboard {
  pubsub?: ContractPubSub | null;
}

export async function getContractDetail(contractUuid: string): Promise<ContractDetail> {
  const res = await apis.get(`/contracts/${contractUuid}`);
  return (res.data?.data ?? res.data) as ContractDetail;
}

export async function markAsContract(
  documentType: 'draft' | 'file', documentUuid: string,
  opts?: { matterUuid?: string | null; projectUuid?: string | null },
): Promise<ContractDetail> {
  const res = await apis.post('/contracts/mark', {
    document_type: documentType, document_uuid: documentUuid,
    matter_uuid: opts?.matterUuid ?? null,
    project_uuid: opts?.projectUuid ?? null,
  });
  return (res.data?.data ?? res.data) as ContractDetail;
}

/** Is a document already tracked as a contract? (drives the draft panel state) */
export async function getContractByDocument(
  documentType: 'draft' | 'file', documentUuid: string,
): Promise<{ tracked: boolean; contract?: ContractRow }> {
  const res = await apis.get(`/contracts/by-document/${documentType}/${documentUuid}`);
  return (res.data?.data ?? res.data) as { tracked: boolean; contract?: ContractRow };
}

export async function getContract(contractUuid: string): Promise<ContractDetail> {
  const res = await apis.get(`/contracts/${contractUuid}`);
  return (res.data?.data ?? res.data) as ContractDetail;
}

export async function unmarkContract(contractUuid: string): Promise<void> {
  await apis.delete(`/contracts/${contractUuid}`);
}

/** Re-run lifecycle-fact extraction (resets the contract to 'pending'). */
export async function extractContract(contractUuid: string): Promise<ContractRow & { queued: boolean }> {
  const res = await apis.post(`/contracts/${contractUuid}/extract`, {});
  return (res.data?.data ?? res.data) as ContractRow & { queued: boolean };
}

export interface ContractFactsUpdate {
  parties?: ContractParty[];
  key_obligations?: string[];
  contract_value?: string | null;
  governing_law?: string | null;
  effective_date?: string | null;
  expiry_date?: string | null;
  renewal_notice_days?: number | null;
  auto_renew?: boolean | null;
}

/** Hand-edit lifecycle facts (partial). */
export async function updateContract(contractUuid: string, facts: ContractFactsUpdate): Promise<ContractDetail> {
  const res = await apis.patch(`/contracts/${contractUuid}`, facts);
  return (res.data?.data ?? res.data) as ContractDetail;
}

/** Link (or unlink with null) the contract to a matter. */
export async function linkContractMatter(contractUuid: string, matterUuid: string | null): Promise<ContractDetail> {
  const res = await apis.post(`/contracts/${contractUuid}/matter`, { matter_uuid: matterUuid });
  return (res.data?.data ?? res.data) as ContractDetail;
}

// ---------------------------------------------------------------------------
// Sharing (Phase 3). A contract is shared with colleagues the same way a
// matter/task/file is - the generic CollaboratorService on the 'contract'
// resource type. These feed the shared manage-access side panel, which
// identifies users by UUID; the backend resolves the UUID to an id.
// ---------------------------------------------------------------------------

export interface ContractCollaborator {
  uuid?: string;
  collaborator_id?: string;
  name?: string;
  email?: string;
  access?: 'view' | 'edit' | 'admin';
  role?: string | null;
  initials?: string | null;
  avatar_url?: string | null;
  is_owner?: boolean;
  source?: string;
}

export async function getContractCollaborators(contractUuid: string): Promise<ContractCollaborator[]> {
  const res = await apis.get(`/contracts/${contractUuid}/collaborators`);
  const data = res.data?.data ?? res.data;
  return (data?.collaborators ?? data ?? []) as ContractCollaborator[];
}

/** Invite a colleague. ``userUuid`` is the user's UUID (the side panel's id). */
export async function addContractCollaborator(
  contractUuid: string, userUuid: string, access: 'view' | 'edit' | 'admin' = 'view',
): Promise<any> {
  const res = await apis.post(`/contracts/${contractUuid}/collaborators`, {
    user_uuid: userUuid, access,
  });
  return res.data?.data ?? res.data;
}

export async function removeContractCollaborator(
  contractUuid: string, collaboratorUuid: string,
): Promise<any> {
  const res = await apis.delete(`/contracts/${contractUuid}/collaborators/${collaboratorUuid}`);
  return res.data?.data ?? res.data;
}

export async function updateContractCollaboratorAccess(
  contractUuid: string, collaboratorUuid: string, access: 'view' | 'edit' | 'admin',
): Promise<any> {
  const res = await apis.patch(`/contracts/${contractUuid}/collaborators/${collaboratorUuid}`, { access });
  return res.data?.data ?? res.data;
}

// ---------------------------------------------------------------------------
// Obligations tracker (Phase 4). Each obligation is a trackable row with a due
// date, owner and status - the nightly renewals engine reminds owners as they
// come due. Extracted key_obligations are auto-promoted to rows on first load.
// ---------------------------------------------------------------------------

export interface ObligationUpdate {
  description?: string;
  due_date?: string | null;
  status?: ObligationStatus;
  owner_user_id?: number | null;
}

export async function getObligations(contractUuid: string): Promise<ContractObligation[]> {
  const res = await apis.get(`/contracts/${contractUuid}/obligations`);
  const data = res.data?.data ?? res.data;
  return (data?.obligations ?? []) as ContractObligation[];
}

export async function addObligation(
  contractUuid: string, description: string, dueDate?: string | null,
): Promise<ContractObligation> {
  const res = await apis.post(`/contracts/${contractUuid}/obligations`, {
    description, due_date: dueDate ?? null,
  });
  return (res.data?.data ?? res.data) as ContractObligation;
}

export async function updateObligation(
  contractUuid: string, obligationUuid: string, fields: ObligationUpdate,
): Promise<ContractObligation> {
  const res = await apis.patch(`/contracts/${contractUuid}/obligations/${obligationUuid}`, fields);
  return (res.data?.data ?? res.data) as ContractObligation;
}

export async function deleteObligation(contractUuid: string, obligationUuid: string): Promise<void> {
  await apis.delete(`/contracts/${contractUuid}/obligations/${obligationUuid}`);
}

/** Edits applied when promoting an obligation to a task (suggest→promote). */
export interface ObligationTaskOverrides {
  title?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  due_date?: string | null;
}

/** Turn one obligation into a trackable task (returns the reflected obligation).
 *  Optional `overrides` let the user edit the task before it's created. */
export async function createObligationTask(
  contractUuid: string, obligationUuid: string, overrides?: ObligationTaskOverrides,
): Promise<ContractObligation> {
  const res = await apis.post(`/contracts/${contractUuid}/obligations/${obligationUuid}/task`, overrides || {});
  return (res.data?.data ?? res.data) as ContractObligation;
}

/** Turn every not-yet-tracked obligation into a task. */
export async function createAllObligationTasks(contractUuid: string): Promise<{ created: number; obligations: ContractObligation[] }> {
  const res = await apis.post(`/contracts/${contractUuid}/obligations/tasks`, {});
  return (res.data?.data ?? res.data) as { created: number; obligations: ContractObligation[] };
}

/** A cross-contract obligation feed item - an obligation plus its contract. */
export interface ObligationFeedItem extends ContractObligation {
  contract_title: string;
  days_to_due: number | null;
}

/** The viewer's open, dated obligations across every accessible contract
 *  (overdue first). Powers the dashboard "Your obligations" view. */
export async function getObligationsFeed(): Promise<ObligationFeedItem[]> {
  const res = await apis.get('/contracts/obligations');
  const data = res.data?.data ?? res.data;
  return (data?.obligations ?? []) as ObligationFeedItem[];
}

/** Firm branding elements for a branded summary export. */
export interface BrandingExportElements {
  firm_name?: string | null;
  logo_data_url?: string | null;
  accent_color?: string | null;
  header_text?: string | null;
  has_branding?: boolean;
}

export async function getBrandingExportElements(templateId?: string | null): Promise<BrandingExportElements> {
  try {
    const res = await apis.get('/contracts/branding-export', {
      params: templateId ? { template_id: templateId } : undefined,
    });
    return (res.data?.data ?? res.data ?? {}) as BrandingExportElements;
  } catch {
    return { has_branding: false };
  }
}

export interface BrandingTemplateOption {
  id: string;
  name: string;
}

/** List the firm's branding templates for the export picker. Best-effort: an
 *  empty list just means the export falls back to plain (no-branding) styling. */
export async function listBrandingTemplates(): Promise<BrandingTemplateOption[]> {
  try {
    const res = await apis.get('/templates/branding/list');
    const raw = res.data?.templates ?? res.data?.data?.templates ?? [];
    return (raw as any[])
      .filter((t) => t && (t.status === 'completed' || t.status === 'ready' || !t.status))
      .map((t) => ({
        id: String(t.uuid ?? t.id ?? t.TemplateID ?? ''),
        name: String(t.name ?? t.Name ?? t.title ?? t.TemplateName ?? 'Branding template'),
      }))
      .filter((t) => t.id);
  } catch {
    return [];
  }
}

/** Create a task to act on this contract's renewal (due on notice/expiry,
 *  assigned to the caller, linked back to the contract). */
export async function createRenewalTask(contractUuid: string): Promise<any> {
  const res = await apis.post(`/contracts/${contractUuid}/renewal-task`, {});
  return res.data?.data ?? res.data;
}
